import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { listCountries, saveCountry, type CountrySetting } from "@/lib/countries.functions";

export const Route = createFileRoute("/_authenticated/admin/lander")({
  component: CountriesAdmin,
  head: () => ({
    meta: [
      { title: "Länder och valutor – admin" },
      { name: "description", content: "Styr vilka länder man kan sälja i och vilken valuta som gäller." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const EMPTY: CountrySetting = {
  code: "",
  nameSv: "",
  nameEn: "",
  nameEs: "",
  currency: "",
  flag: "",
  active: true,
  sortOrder: 100,
};

function CountriesAdmin() {
  const qc = useQueryClient();
  const read = useServerFn(listCountries);
  const write = useServerFn(saveCountry);
  const { data } = useQuery({
    queryKey: ["countries", "all"],
    queryFn: () => read({ data: { all: true } }),
  });
  const [draft, setDraft] = useState<CountrySetting>(EMPTY);

  const save = useMutation({
    mutationFn: (c: CountrySetting) => write({ data: c }),
    onSuccess: () => {
      toast.success("Sparat.");
      qc.invalidateQueries({ queryKey: ["countries"] });
      setDraft(EMPTY);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <AdminShell title="Länder och valutor">
      <p className="text-sm text-muted-foreground">
        Länderna här kan väljas när en annons läggs upp. Valutan följer landet. Slår du av ett land
        går det inte att välja längre – befintliga annonser påverkas inte.
      </p>

      <div className="mt-6 space-y-3">
        {(data ?? []).map((c) => (
          <CountryRow key={c.code} country={c} onSave={(next) => save.mutate(next)} />
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-border p-4">
        <p className="text-sm font-medium">Lägg till land</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <Input placeholder="Kod (SE)" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
          <Input placeholder="Valuta (SEK)" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} />
          <Input placeholder="Flagga 🇸🇪" value={draft.flag} onChange={(e) => setDraft({ ...draft, flag: e.target.value })} />
          <Input placeholder="Namn på svenska" value={draft.nameSv} onChange={(e) => setDraft({ ...draft, nameSv: e.target.value })} />
          <Input placeholder="Namn på engelska" value={draft.nameEn} onChange={(e) => setDraft({ ...draft, nameEn: e.target.value })} />
          <Input placeholder="Namn på spanska" value={draft.nameEs} onChange={(e) => setDraft({ ...draft, nameEs: e.target.value })} />
        </div>
        <Button className="mt-3" onClick={() => save.mutate(draft)} disabled={save.isPending}>
          Lägg till
        </Button>
      </div>
    </AdminShell>
  );
}

function CountryRow({
  country,
  onSave,
}: {
  country: CountrySetting;
  onSave: (c: CountrySetting) => void;
}) {
  const [row, setRow] = useState(country);
  const dirty = JSON.stringify(row) !== JSON.stringify(country);

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xl leading-none" aria-hidden>
          {row.flag}
        </span>
        <span className="text-sm font-medium">{row.code}</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Aktivt</span>
          <Switch
            checked={row.active}
            onCheckedChange={(active) => {
              setRow({ ...row, active });
              onSave({ ...row, active });
            }}
            aria-label={`Aktivera ${row.code}`}
          />
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-4">
        <Input value={row.nameSv} onChange={(e) => setRow({ ...row, nameSv: e.target.value })} aria-label="Namn på svenska" />
        <Input value={row.nameEn} onChange={(e) => setRow({ ...row, nameEn: e.target.value })} aria-label="Namn på engelska" />
        <Input value={row.nameEs} onChange={(e) => setRow({ ...row, nameEs: e.target.value })} aria-label="Namn på spanska" />
        <Input value={row.currency} onChange={(e) => setRow({ ...row, currency: e.target.value })} aria-label="Valuta" />
      </div>
      {dirty && (
        <Button size="sm" className="mt-3" onClick={() => onSave(row)}>
          Spara
        </Button>
      )}
    </div>
  );
}
